import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import axios from 'axios';
import { useNotify } from 'react-admin';
import { apiUrl } from '../../dataProvider';
import { handleAPIError } from '../Signet/Studio/hooks/useEngravingAPI';

// Create context
const TextureContext = createContext();

export const TextureProvider = ({ children }) => {
  const [textures, setTextures] = useState({
    signet: null,
    band: null,
    sleeve: null
  });
  const [loading, setLoading] = useState(false);
  // Create separate cache for each texture type
  const textureCaches = useRef({
    signet: new Map(),
    band: new Map(),
    sleeve: new Map()
  });
  const notify = useNotify();

  const preloadTexture = useCallback(async (url, type = 'signet') => {
    // Get the appropriate cache for this texture type
    const cache = textureCaches.current[type];
    
    if (!url || cache.has(url)) {
      return cache.get(url);
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        cache.set(url, dataUrl);
        resolve(dataUrl);
      };

      img.onerror = reject;
      img.src = url;
    });
  }, []);

  const getTextureFromCache = useCallback(async (url, type = 'signet') => {
    if (!url) return null;
    
    // Get the appropriate cache for this texture type
    const cache = textureCaches.current[type];
    
    if (cache.has(url)) {
      return cache.get(url);
    }
    
    try {
      const dataUrl = await preloadTexture(url, type);
      return dataUrl;
    } catch (error) {
      console.warn(`Failed to load texture: ${url}`, error);
      return null;
    }
  }, [preloadTexture]);

  const fetchTextureImages = useCallback(async (type = 'signet') => {
    if (textures[type]) return textures[type];
    setLoading(true);

    try {
      const response = await axios.get(`${apiUrl}/texture-images?type=${type}`);
      if (response?.data?.success) {
        const textureData = response.data.data;
        
        setTextures(prevTextures => ({
          ...prevTextures,
          [type]: textureData
        }));

        // Start background preloading without awaiting
        (async () => {
          const batchSize = 15; // Process 15 images at a time
          const urls = Object.values(textureData).map(({ preview_url }) => preview_url);

          // Process images in batches
          for (let i = 0; i < urls.length; i += batchSize) {
            const batch = urls.slice(i, i + batchSize);

            // Process batch in parallel
            await Promise.all(
              batch.map((url) =>
                preloadTexture(url, type).catch((error) => {
                  console.warn('Failed to preload texture image:', error);
                }),
              ),
            );
          }
        })();
        
        return textureData;
      }
    } catch (error) {
      handleAPIError(error, notify);
      return null;
    } finally {
      setLoading(false);
    }
  }, [textures, preloadTexture, notify]);

  const value = {
    textures,
    loading,
    preloadTexture,
    getTextureFromCache,
    fetchTextureImages,
  };

  return <TextureContext.Provider value={value}>{children}</TextureContext.Provider>;
};

// Hook to access Texture context
export const useTexture = () => {
  const context = useContext(TextureContext);
  if (!context) {
    throw new Error('useTexture must be used within a TextureProvider');
  }
  return context;
}; 